import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Card from "react-bootstrap/Card";
import Form from "react-bootstrap/Form";
import Badge from "react-bootstrap/Badge";
import Alert from "react-bootstrap/Alert";
import Spinner from "react-bootstrap/Spinner";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import Button from "react-bootstrap/Button";
import Axios from "axios";
import { Store } from "../Store";
import { toast } from "react-toastify";
import { getError } from "../utils";
import { api } from "../Api";
import { useOtpGate, OtpGateModal } from "../component/OtpGate";
import SecurityBadges from "../component/SecurityBadges";

// Every balance a member can convert FROM. The destination is never a
// choice — every source lands in Available Balance, except Shopping Point,
// which has always landed in Register Point instead (see `destination`
// below). `policyKey` names the Money Controls switch that turns each one on;
// `mode: "full"` means the whole pending pool moves at once (nothing typed),
// `mode: "typed"` means the member enters an amount up to their balance.
// `icon` is a Font Awesome glyph so each tile reads at a glance, matching the
// icon-per-balance convention the app already uses on the mobile side.
const SOURCE_CATALOG = [
  {
    key: "point",
    label: "Register Point",
    policyKey: "convertSourceRegisterEnabled",
    mode: "typed",
    icon: "fa-user-plus",
  },
  {
    key: "shoppingpoint",
    label: "Shopping Point",
    policyKey: "convertSourceShoppingEnabled",
    mode: "typed",
    icon: "fa-shopping-bag",
  },
  {
    key: "shoppingbalance",
    label: "Shopping Balance",
    policyKey: "convertSourceShoppingBalanceEnabled",
    mode: "typed",
    icon: "fa-credit-card",
  },
  {
    key: "matching",
    label: "Group Sales",
    policyKey: "convertSourceMatchingEnabled",
    mode: "full",
    icon: "fa-sitemap",
  },
  {
    key: "global",
    // Same balance Withdraw calls "Global Sales" — Convert calls it this instead.
    label: "Profit Incentive (PI)",
    policyKey: "convertSourceGlobalEnabled",
    mode: "typed",
    icon: "fa-line-chart",
  },
  {
    key: "incomegeneration",
    label: "Income Generation",
    policyKey: "convertSourceIncomeGenerationEnabled",
    mode: "typed",
    icon: "fa-layer-group",
  },
  {
    key: "referencepoint",
    label: "Reference Point",
    policyKey: "convertSourceReferenceEnabled",
    mode: "typed",
    icon: "fa-link",
  },
  {
    key: "psipoint",
    label: "Profit Sharing Incentive (PSI)",
    policyKey: "convertSourcePsiEnabled",
    mode: "typed",
    icon: "fa-pie-chart",
  },
];

const DEFAULT_POLICY = {
  convertEnabled: true,
  convertDisabledMessage: "",
  minimumConvert: 0,
  maximumConvert: 0,
  convertCharge: 0,
  convertSourceShoppingEnabled: true,
  convertSourceMatchingEnabled: true,
  convertSourceGlobalEnabled: false,
  convertSourceIncomeGenerationEnabled: false,
  convertSourceRegisterEnabled: true,
  convertSourceReferenceEnabled: false,
  convertSourcePsiEnabled: true,
  convertSourceShoppingBalanceEnabled: true,
};

const round2 = (v) => Math.round((Number(v) || 0) * 100) / 100;

// Animates a number from wherever it last was to `value` — the "live" feel
// on the amount/fee/destination figures, without pulling in a library.
function useCountUp(value, durationMs = 450) {
  const [display, setDisplay] = useState(value);
  const fromRef = useRef(value);

  useEffect(() => {
    const from = fromRef.current;
    const to = value;
    if (from === to) return;

    let raf;
    const start = performance.now();
    const step = (now) => {
      const t = Math.min((now - start) / durationMs, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      const current = from + (to - from) * eased;
      setDisplay(current);
      if (t < 1) {
        raf = requestAnimationFrame(step);
      } else {
        fromRef.current = to;
      }
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, durationMs]);

  return display;
}

// Scoped keyframes for the tile hover/selection feel and the flowing arrow —
// kept local to this screen rather than added to the shared stylesheet.
const CONVERT_ANIMATION_STYLES = `
  .convert-tile {
    transition: transform 0.15s ease, border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease;
    cursor: pointer;
  }
  .convert-tile:hover:not(.convert-tile-disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 10px rgba(0,0,0,0.08);
  }
  .convert-tile:active:not(.convert-tile-disabled) {
    transform: translateY(0);
  }
  .convert-tile-selected {
    animation: convertTilePop 0.25s ease;
  }
  @keyframes convertTilePop {
    0% { transform: scale(0.96); }
    60% { transform: scale(1.02); }
    100% { transform: scale(1); }
  }
  .convert-flow-arrow {
    transition: opacity 0.2s ease, color 0.2s ease;
  }
  .convert-flow-arrow-active {
    animation: convertFlowBounce 1.3s ease-in-out infinite;
  }
  @keyframes convertFlowBounce {
    0%, 100% { transform: translateY(0); opacity: 0.55; }
    50% { transform: translateY(5px); opacity: 1; }
  }
  .convert-destination {
    transition: border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease;
  }
  .convert-destination-live {
    box-shadow: 0 0 0 4px rgba(25, 135, 84, 0.12);
  }
  @keyframes convertPulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.55; }
  }
  .convert-submitting {
    animation: convertPulse 1s ease-in-out infinite;
  }
`;

export default function ConvertScreen() {
  const { state } = useContext(Store);
  const { userInfo } = state;
  const navigate = useNavigate();

  const [balances, setBalances] = useState({
    point: 0,
    shoppingpoint: 0,
    shoppingbalance: 0,
    matching: 0,
    global: 0,
    incomegeneration: 0,
    referencepoint: 0,
    psipoint: 0,
  });
  const [availableBalance, setAvailableBalance] = useState(0);
  // Money Controls, served alongside the balances. Defaults keep the screen
  // usable if an older backend does not send the block at all.
  const [policy, setPolicy] = useState(DEFAULT_POLICY);
  const [convertFrom, setConvertFrom] = useState("");
  const [amount, setAmount] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [justSelected, setJustSelected] = useState("");

  const fetchData = async () => {
    try {
      setFetching(true);
      const { data } = await Axios.get(
        api + `/api/users/point/${userInfo._id}`,
        { headers: { authorization: `Bearer ${userInfo.token}` } },
      );
      if (data.moneyPolicy) setPolicy({ ...DEFAULT_POLICY, ...data.moneyPolicy });
      setAvailableBalance(data.availablebalance || 0);
      setBalances({
        point: data.point || 0,
        shoppingpoint: data.shoppingpoint || 0,
        shoppingbalance: data.shoppingbalance || 0,
        matching:
          data.matchingPoint?.length > 0
            ? data.matchingPoint.reduce((sum, item) => sum + item.amount, 0)
            : 0,
        global: data.globalSales || 0,
        incomegeneration: data.incomeGeneration || 0,
        referencepoint: data.referencePoints || 0,
        psipoint: data.psipoint || 0,
      });
    } catch (error) {
      toast.error(getError(error));
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo]);

  const otpGate = useOtpGate({
    path: "convert",
    token: userInfo.token,
    onSuccess: (data) => {
      toast.success(data?.message || "Conversion completed.");
      fetchData();
      setConvertFrom("");
      setAmount("");
      setPassword("");
    },
    onError: (error) => toast.error(getError(error)),
  });

  // Sources whose Money Controls switch is on — everything the form and the
  // balance summary render from is derived off this one list.
  const enabledSources = useMemo(
    () => SOURCE_CATALOG.filter((s) => policy[s.policyKey]),
    [policy],
  );

  const selectedSource = enabledSources.find((s) => s.key === convertFrom);
  const isFullPool = selectedSource?.mode === "full";
  const sourceBalance = convertFrom ? balances[convertFrom] || 0 : 0;
  const enteredAmount = isFullPool ? sourceBalance : Number(amount) || 0;

  const convertCharge = Number(policy.convertCharge) || 0;
  const feeAmount = round2(
    convertCharge > 0 ? (enteredAmount * convertCharge) / 100 : 0,
  );
  const netAmount = round2(enteredAmount - feeAmount);

  // Every source lands in Available Balance — except Shopping Point and
  // Shopping Balance, which land in Register Point only.
  const destination =
    convertFrom === "shoppingpoint" || convertFrom === "shoppingbalance"
      ? { label: "Register Point", value: balances.point || 0 }
      : { label: "Available Balance", value: availableBalance };

  // Live, animated figures — the "watch it move" feel as the amount changes.
  const animatedAmount = useCountUp(enteredAmount);
  const animatedFee = useCountUp(feeAmount);
  const animatedNet = useCountUp(netAmount);
  const animatedDestination = useCountUp(destination.value);

  const selectSource = (key) => {
    if (convertFrom === key) return;
    setConvertFrom(key);
    setJustSelected(key);
    const meta = enabledSources.find((s) => s.key === key);
    setAmount(meta?.mode === "full" ? String(balances[key] || 0) : "");
    window.setTimeout(() => setJustSelected(""), 260);
  };

  const useFullBalance = () => setAmount(String(sourceBalance || ""));

  const convert = async () => {
    if (!policy.convertEnabled) {
      toast.error(
        policy.convertDisabledMessage ||
          "Point conversion is temporarily turned off. Please try again later.",
      );
      return;
    }
    if (!selectedSource) {
      toast.error("Please choose which balance to convert.");
      return;
    }
    if (!enteredAmount || enteredAmount <= 0) {
      toast.error(
        isFullPool
          ? `You don't have any ${selectedSource.label} to convert right now.`
          : "Please enter an amount greater than 0.",
      );
      return;
    }
    if (enteredAmount > sourceBalance) {
      toast.error(
        `Not enough ${selectedSource.label}. You have ${sourceBalance}.`,
      );
      return;
    }
    if (policy.minimumConvert > 0 && enteredAmount < policy.minimumConvert) {
      toast.error(`Minimum convert amount is ${policy.minimumConvert}.`);
      return;
    }
    if (policy.maximumConvert > 0 && enteredAmount > policy.maximumConvert) {
      toast.error(`Maximum convert amount is ${policy.maximumConvert}.`);
      return;
    }
    if (!password || password.length < 4) {
      toast.error("Please enter your password (at least 4 characters).");
      return;
    }

    setLoading(true);
    await otpGate.start({
      from: convertFrom,
      amount: enteredAmount,
      password: password,
    });
    setLoading(false);
  };

  function gotoHome() {
    navigate("/transferandhistory");
  }

  const flowing = enteredAmount > 0;

  return (
    <div>
      <Helmet>
        <title>Convert</title>
      </Helmet>
      <style>{CONVERT_ANIMATION_STYLES}</style>
      <OtpGateModal
        gate={otpGate}
        title="Confirm this conversion"
        actionLabel="Confirm conversion"
      />
      <Container className="no-gutter">
        <div className="card">
          <Row>
            <Col style={{ textAlign: "center" }}>
              <img
                src="https://res.cloudinary.com/dddogyqqa/image/upload/v1670435815/logo/clickcart-logo_wfktlg.png"
                style={{ width: "15rem", margin: "2rem" }}
                alt="ClickCart Logo"
              />
            </Col>
          </Row>
          <div className="card-header" onClick={gotoHome} role="button">
            <i className="fa fa-long-arrow-left" aria-hidden="true"></i>{" "}
            {"  "} Convert
          </div>

          <div className="card-body w-90" style={{ margin: "auto" }}>
            {fetching ? (
              <div className="text-center py-4">
                <Spinner animation="border" size="sm" /> Loading your
                balances&hellip;
              </div>
            ) : (
              <>
                {!policy.convertEnabled && (
                  <Alert variant="danger" className="py-2">
                    {policy.convertDisabledMessage ||
                      "Point conversion is temporarily turned off. Please try again later."}
                  </Alert>
                )}

                {policy.convertEnabled && enabledSources.length === 0 && (
                  <Alert variant="secondary" className="py-2">
                    No balances are open for conversion right now. Please
                    check back later.
                  </Alert>
                )}

                {policy.convertEnabled &&
                  (policy.minimumConvert > 0 ||
                    policy.maximumConvert > 0 ||
                    convertCharge > 0) && (
                    <Alert variant="info" className="py-2 small mb-3">
                      {policy.minimumConvert > 0 &&
                        `Minimum: ${policy.minimumConvert}. `}
                      {policy.maximumConvert > 0 &&
                        `Maximum: ${policy.maximumConvert}. `}
                      {convertCharge > 0
                        ? `Charge: ${convertCharge}% of the amount, deducted before crediting ${destination.label}.`
                        : "No conversion charge."}
                    </Alert>
                  )}

                {/* Tap a balance to select it — the tile itself is the
                    control, so there is nothing to open, scroll, or match up
                    with a separate dropdown. Flows down into a single,
                    unmistakable destination. */}
                <Card className="border-0 bg-white shadow-sm mb-3">
                  <Card.Body className="py-3">
                    <div className="text-muted small mb-2">
                      Tap a balance to convert from
                    </div>
                    <Row className="g-2">
                      {enabledSources.map((s) => {
                        const active = convertFrom === s.key;
                        return (
                          <Col xs={6} md={4} key={s.key}>
                            <div
                              role="radio"
                              aria-checked={active}
                              tabIndex={0}
                              onClick={() => selectSource(s.key)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  selectSource(s.key);
                                }
                              }}
                              className={
                                "convert-tile border rounded p-2 text-center h-100" +
                                (active ? " border-primary bg-light" : "") +
                                (active && justSelected === s.key
                                  ? " convert-tile-selected"
                                  : "")
                              }
                            >
                              <div>
                                <i
                                  className={`fa ${s.icon}`}
                                  aria-hidden="true"
                                  style={{
                                    color: active ? "#0d6efd" : "#6c757d",
                                  }}
                                ></i>
                              </div>
                              <div className="text-muted small">{s.label}</div>
                              <div className="fw-semibold">
                                {balances[s.key] || 0}
                              </div>
                            </div>
                          </Col>
                        );
                      })}
                    </Row>

                    <div className="text-center my-2">
                      <i
                        className={
                          "fa fa-long-arrow-down convert-flow-arrow" +
                          (flowing
                            ? " convert-flow-arrow-active text-success"
                            : " text-muted")
                        }
                        aria-hidden="true"
                        style={{ fontSize: "1.1rem" }}
                      ></i>
                    </div>

                    <div
                      className={
                        "convert-destination rounded p-3 text-center" +
                        (flowing ? " convert-destination-live" : "")
                      }
                      style={{
                        border: "2px solid #198754",
                        backgroundColor: "rgba(25, 135, 84, 0.08)",
                      }}
                    >
                      <Badge bg="success" className="mb-1">
                        Destination
                      </Badge>
                      <div className="fw-semibold">{destination.label}</div>
                      <div className="fs-5 fw-bold text-success">
                        {Math.round(animatedDestination)}
                      </div>
                    </div>
                  </Card.Body>
                </Card>

                <Form.Group className="mb-2">
                  <div className="d-flex align-items-center justify-content-between mb-1">
                    <Form.Label className="small text-muted mb-0">
                      Amount
                    </Form.Label>
                    {selectedSource && !isFullPool && sourceBalance > 0 && (
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 small"
                        onClick={useFullBalance}
                      >
                        Use full balance ({sourceBalance})
                      </Button>
                    )}
                  </div>
                  <Form.Control
                    type="number"
                    placeholder="0"
                    value={isFullPool ? sourceBalance : amount}
                    readOnly={isFullPool}
                    disabled={!selectedSource}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                  {isFullPool && selectedSource && (
                    <div className="form-text">
                      {selectedSource.label} converts as a whole — the full
                      pending balance moves at once.
                    </div>
                  )}
                </Form.Group>

                {selectedSource && enteredAmount > 0 && (
                  <div className="mb-3">
                    <Badge bg="light" text="dark" className="border me-1">
                      Amount: {round2(animatedAmount)}
                    </Badge>
                    {convertCharge > 0 && (
                      <Badge bg="light" text="dark" className="border me-1">
                        Fee ({convertCharge}%): {round2(animatedFee)}
                      </Badge>
                    )}
                    <Badge bg="success">
                      You'll receive: {round2(animatedNet)} in {destination.label}
                    </Badge>
                  </div>
                )}

                <Form.Group className="mb-3">
                  <Form.Label className="small text-muted mb-1">
                    Password
                  </Form.Label>
                  <Form.Control
                    type="password"
                    placeholder="Your account password"
                    disabled={!policy.convertEnabled}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </Form.Group>

                <Button
                  variant="outline-success w-100"
                  className={loading ? "convert-submitting" : ""}
                  disabled={
                    Boolean(loading) ||
                    !policy.convertEnabled ||
                    enabledSources.length === 0
                  }
                  onClick={convert}
                >
                  {loading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                      />{" "}
                      Converting&hellip;
                    </>
                  ) : (
                    "Convert"
                  )}
                </Button>
                <SecurityBadges variant="inline" />
              </>
            )}
          </div>

          <div style={{ height: "51vh" }}> {""}</div>
        </div>
      </Container>
    </div>
  );
}
